import { Scheme } from '../types/client';
import { SCHEME_REGION_GLOBAL } from '../config/constants';

export const isGlobalScheme = (scheme?: { region?: number | null } | null) =>
  scheme?.region === SCHEME_REGION_GLOBAL;

/** The selection is one region (see `isSchemeSelectable`), so its first scheme decides. */
export function isGlobalSelection(schemeIds: number[], schemes: Scheme[]): boolean {
  const firstSelected = schemes.find(s => s.id === schemeIds[0]);
  return Boolean(firstSelected && isGlobalScheme(firstSelected));
}

export function getSelectedGroupValue(schemeIds: number[], schemes: Scheme[]): number | null {
  if (schemeIds.length === 0) return null;
  const firstSelected = schemes.find(s => s.id === schemeIds[0]);
  return firstSelected?.order_calc_type ?? null;
}

/**
 * Whether `scheme` can join the current selection.
 *
 * One form submits ONE set of parameters to every selected scheme, so the selection must
 * share an `order_calc_type` (whole shares vs fractional units), a `base_currency` —
 * Limit Price and Min Order Value are denominated, so one number cannot serve an INR and
 * a USD scheme at once — and a region, since global orders take different limits.
 */
export function isSchemeSelectable(
  scheme: Scheme,
  schemeIds: number[],
  schemes: Scheme[],
): boolean {
  if (schemeIds.length === 0) return true;

  const firstSelected = schemes.find(s => s.id === schemeIds[0]);
  if (!firstSelected) return true;

  if (scheme.order_calc_type !== firstSelected.order_calc_type) return false;
  if (isGlobalScheme(scheme) !== isGlobalScheme(firstSelected)) return false;

  // A payload without base_currency degrades to order_calc_type only, rather than
  // disabling every scheme.
  if (firstSelected.base_currency && scheme.base_currency) {
    return scheme.base_currency === firstSelected.base_currency;
  }
  return true;
}

/** The set "select all" operates on, so it can never tick a disabled scheme. */
export function getSelectableSchemes(schemeIds: number[], schemes: Scheme[]): Scheme[] {
  return schemes.filter(scheme => isSchemeSelectable(scheme, schemeIds, schemes));
}

/** An investment strategy that at least one of the given schemes belongs to. */
export interface StrategyOption {
  id: number;
  name: string;
}

/**
 * Distinct investment strategies present on `schemes`, sorted by name.
 *
 * Derived from the schemes already loaded by the form rather than fetched
 * separately, so a strategy with no selectable scheme never shows up.
 */
export function getStrategyOptions(schemes: Scheme[]): StrategyOption[] {
  const byId = new Map<number, StrategyOption>();
  schemes.forEach(scheme => {
    const id = Number(scheme.investment_strategy);
    if (!scheme.investment_strategy || isNaN(id) || byId.has(id)) return;
    byId.set(id, { id, name: scheme.investment_strategy_display || `Strategy ${id}` });
  });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export interface StrategySchemeSelection {
  schemeIds: number[];
  /**
   * How many schemes in the strategy were left out because they could not join the
   * selection — a different order calculation type or a different base currency.
   * Non-zero means the caller must tell the user — otherwise picking a strategy
   * quietly selects less than it says it does.
   */
  omittedCount: number;
}

/**
 * Schemes belonging to `strategyId` that can be ordered together.
 *
 * A strategy can span order calculation types and base currencies, and an order cannot
 * mix either. The first matching scheme anchors the pick and the rest are filtered with
 * `isSchemeSelectable`, so a strategy shortcut can only ever build a selection the
 * checkbox rules would also allow; the omitted ones stay unchecked and disabled.
 */
export function getSchemeIdsForStrategy(
  strategyId: number,
  schemes: Scheme[],
): StrategySchemeSelection {
  const matching = schemes.filter(s => Number(s.investment_strategy) === strategyId);
  if (matching.length === 0) return { schemeIds: [], omittedCount: 0 };

  const anchor = matching[0];
  const schemeIds = matching
    .filter(scheme => isSchemeSelectable(scheme, [anchor.id], matching))
    .map(s => s.id);
  return { schemeIds, omittedCount: matching.length - schemeIds.length };
}
